import React from 'react';
import { ModeRestrictions } from '../../hooks/useModeRestrictions';
import CustomSelect from '../ui/CustomSelect';

interface SettingsPanelProps {
  prompt: string;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  extractionType: string;
  setExtractionType: React.Dispatch<React.SetStateAction<string>>;
  mode: string;
  setMode: React.Dispatch<React.SetStateAction<string>>;
  modeRestrictions: ModeRestrictions;
  showAdvancedSettings: boolean;
  setShowAdvancedSettings: React.Dispatch<React.SetStateAction<boolean>>;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  prompt,
  setPrompt,
  extractionType,
  setExtractionType,
  mode,
  setMode,
  modeRestrictions,
  showAdvancedSettings,
  setShowAdvancedSettings
}) => {
  const extractionTypes = [
    { value: 'full-outfit', label: 'Full Outfit' },
    { value: 'individual-items', label: 'Individual Items' },
    { value: 'accessories', label: 'Accessories Only' },
    { value: 'upper-body', label: 'Upper Body' },
    { value: 'lower-body', label: 'Lower Body' }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-text-heading mb-1">
            Extraction Type
            <span className="ml-2 text-xs text-text-description">(Fixed)</span>
          </label>
          <CustomSelect
            value={extractionType}
            onChange={(value) => setExtractionType(value)}
            disabled={true}
            options={extractionTypes.map(type => ({
              value: type.value,
              label: type.label
            }))}
            className="w-full"
          />
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-text-heading mb-1">
            Mode
            {!modeRestrictions.hasInitialLoad && (
              <span className="ml-2 text-xs text-text-description">(Loading...)</span>
            )}
          </label>
          <div className="relative">
            <CustomSelect
              value={mode}
              onChange={(value) => setMode(value)}
              options={modeRestrictions.availableModes.map((modeOption) => {
                let planRequirement = '';
                if (modeOption.disabled && modeOption.requiresPlan) {
                  const minPlan = modeOption.requiresPlan[0];
                  switch (minPlan) {
                    case 'plus':
                      planRequirement = ' (Plus Plan Required)';
                      break;
                    case 'pro':
                      planRequirement = ' (Pro Plan Required)';
                      break;
                    case 'enterprise':
                      planRequirement = ' (Enterprise Plan Required)';
                      break;
                    default:
                      planRequirement = ' (Upgrade Required)';
                  }
                }
                return {
                  value: modeOption.value,
                  label: `${modeOption.label}${planRequirement}`,
                  disabled: modeOption.disabled
                };
              })}
              disabled={!modeRestrictions.hasInitialLoad}
              className="w-full"
            />
            {modeRestrictions.error && (
              <p className="text-xs text-red-600 mt-1">{modeRestrictions.error}</p>
            )}
            {!modeRestrictions.canUseMode(mode) && modeRestrictions.hasInitialLoad && (
              <p className="text-xs text-orange-600 mt-1">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                {modeRestrictions.getRestrictedModeMessage(mode)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="border-t border-accent-taupe pt-3">
        <button
          type="button"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
          className="flex items-center justify-between w-full text-left text-sm font-medium text-text-heading hover:text-text-heading transition-colors"
        >
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Advanced Settings
          </span>
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${showAdvancedSettings ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAdvancedSettings && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-text-heading mb-1">Custom Instructions (Optional)</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Extract top from image and show top over white background in product photography style..."
              className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 min-h-[80px] resize-y text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPanel; 